import type { User } from "@/interfaces/User";

export interface AuthData {
  token: string;
  user: User;
  expiresAt: number;
}

export interface SessionValidationModal {
  show: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

class AuthService {
  private readonly TOKEN_KEY = "tf_token";
  private readonly USER_KEY = "tf_user";
  private readonly EXPIRES_KEY = "tf_expires";

  // Timeouts para validación de sesión
  private sessionTimeout: ReturnType<typeof setTimeout> | null = null;
  private warningTimeout: ReturnType<typeof setTimeout> | null = null;
  private readonly SESSION_WARNING_TIME = 10 * 60 * 1000; // 10 minutos antes de expirar
  // private readonly SESSION_TIMEOUT = 10 * 1000; // 10 segundos para responder

  // Callbacks para manejar eventos de sesión
  private onSessionExpiring: ((timeLeft: number) => void) | null = null;
  private onSessionExpired: (() => void) | null = null;

  /**
   * Guarda los datos de autenticación en localStorage
   */
  saveAuthData(authData: AuthData): void {
    try {
      const expiresAt = Date.now() + 12 * 60 * 60 * 1000; // 12 horas desde ahora

      // Validar que el usuario exista
      if (!authData.user) {
        throw new Error("Usuario no válido");
      }

      localStorage.setItem(this.TOKEN_KEY, authData.token);
      localStorage.setItem(this.USER_KEY, JSON.stringify(authData.user));
      localStorage.setItem(this.EXPIRES_KEY, expiresAt.toString());

      // Iniciar monitoreo de sesión
      this.startSessionMonitoring();
    } catch (error) {
      // Limpiar datos parciales si hay error
      this.clearAuthData();
    }
  }

  /**
   * Obtiene el token actual
   */
  getToken(): string | null {
    try {
      if (this.isTokenExpired()) {
        this.clearAuthData();
        return null;
      }
      return localStorage.getItem(this.TOKEN_KEY);
    } catch (error) {
      return null;
    }
  }

  /**
   * Obtiene los datos del usuario actual
   */
  getUser(): User | null {
    try {
      const userData = localStorage.getItem(this.USER_KEY);
      if (!userData || userData === "undefined" || userData === "null") {
        return null;
      }
      return JSON.parse(userData);
    } catch (error) {
      return null;
    }
  }

  /**
   * Verifica si el token ha expirado
   */
  isTokenExpired(): boolean {
    try {
      const expiresAt = localStorage.getItem(this.EXPIRES_KEY);
      if (!expiresAt) return true;

      return Date.now() >= parseInt(expiresAt);
    } catch (error) {
      return true;
    }
  }

  /**
   * Verifica si el usuario está autenticado
   */
  isAuthenticated(): boolean {
    return !!(this.getToken() && this.getUser() && !this.isTokenExpired());
  }

  /**
   * Limpia todos los datos de autenticación
   */
  clearAuthData(): void {
    try {
      localStorage.removeItem(this.TOKEN_KEY);
      localStorage.removeItem(this.USER_KEY);
      localStorage.removeItem(this.EXPIRES_KEY);

      // Limpiar timeouts
      this.stopSessionMonitoring();
    } catch (error) {
      console.error("Error limpiando datos de autenticación:", error);
    }
  }

  /**
   * Realiza logout completo
   * Nota: El backend de Pescetti no tiene endpoint de logout, solo limpiamos datos locales
   */
  async logout(): Promise<void> {
    // Limpiar datos locales (el backend usa JWT stateless, no requiere logout)
    this.clearAuthData();
  }

  /**
   * Inicia el monitoreo de la sesión
   */
  private startSessionMonitoring(): void {
    this.stopSessionMonitoring();

    const expiresAt = localStorage.getItem(this.EXPIRES_KEY);
    if (!expiresAt) return;

    const expirationTime = parseInt(expiresAt);
    const warningTime = expirationTime - this.SESSION_WARNING_TIME;
    const timeUntilWarning = warningTime - Date.now();

    if (timeUntilWarning > 0) {
      this.warningTimeout = setTimeout(() => {
        this.showSessionWarning();
      }, timeUntilWarning);
    }

    this.sessionTimeout = setTimeout(() => {
      this.handleSessionExpired();
    }, expirationTime - Date.now());
  }

  /**
   * Detiene el monitoreo de la sesión
   */
  private stopSessionMonitoring(): void {
    if (this.warningTimeout) {
      clearTimeout(this.warningTimeout);
      this.warningTimeout = null;
    }
    if (this.sessionTimeout) {
      clearTimeout(this.sessionTimeout);
      this.sessionTimeout = null;
    }
  }

  /**
   * Muestra advertencia de sesión próxima a expirar
   */
  private showSessionWarning(): void {
    const expiresAt = localStorage.getItem(this.EXPIRES_KEY);
    if (!expiresAt) return;

    const timeLeft = parseInt(expiresAt) - Date.now();
    if (this.onSessionExpiring) {
      this.onSessionExpiring(timeLeft);
    }
  }

  /**
   * Maneja la expiración de la sesión
   */
  private handleSessionExpired(): void {
    this.clearAuthData();
    if (this.onSessionExpired) {
      this.onSessionExpired();
    }
  }

  /**
   * Configura callbacks para eventos de sesión
   */
  setSessionCallbacks(callbacks: {
    onSessionExpiring?: (timeLeft: number) => void;
    onSessionExpired?: () => void;
  }): void {
    this.onSessionExpiring = callbacks.onSessionExpiring || null;
    this.onSessionExpired = callbacks.onSessionExpired || null;
  }

  /**
   * Valida la sesión con el usuario (como apps bancarias)
   */
  validateSessionWithUser(): Promise<boolean> {
    return new Promise((resolve) => {
      const expiresAt = localStorage.getItem(this.EXPIRES_KEY);
      if (!expiresAt) {
        resolve(false);
        return;
      }

      const timeLeft = parseInt(expiresAt) - Date.now();

      // Si quedan menos de 10 minutos, mostrar modal de validación
      if (timeLeft < this.SESSION_WARNING_TIME) {
        this.showSessionValidationModal(resolve);
      } else {
        resolve(true);
      }
    });
  }

  /**
   * Muestra modal de validación de sesión
   */
  private showSessionValidationModal(resolve: (value: boolean) => void): void {
    // Delegar a la UI: simplemente invocar callback de expiración cercana
    const expiresAt = localStorage.getItem(this.EXPIRES_KEY);
    if (!expiresAt) {
      resolve(false);
      return;
    }
    const timeLeft = parseInt(expiresAt) - Date.now();
    if (this.onSessionExpiring) {
      this.onSessionExpiring(timeLeft);
    }
    // Por defecto, si no hay UI suscripta, considerar válida
    resolve(true);
  }
}

// Instancia singleton
export const authService = new AuthService();

// Configurar callbacks por defecto
authService.setSessionCallbacks({
  onSessionExpiring: (timeLeft: number) => {
    console.log(
      `Sesión expirando en ${Math.round(timeLeft / 1000 / 60)} minutos`
    );
  },
  onSessionExpired: () => {
    // Redirigir al login
    window.location.href = "/login";
  },
});
